import { buildArrayMap, buildObjectMap } from "@cervval.js/common/src/tools/collection.map.tools";
import { INIT_ID } from "@cervval.js/common/src/tools/dto.tools";
import { toTimestamp } from "@cervval.js/common/src/tools/timestamps.tools.definitions";
import { NOW } from "@cervval.js/common/src/tools/timestamps.tools.factory";
import { _SAFE_SHARING, BOOKING_USER_TYPE } from "@ehealth_tools/common-booking/src/data/booking.types";
import { ID } from "@ehealth_tools/common/src/data/propertyTypes/property.types";
import { CoachRegistrationParams } from "../../utils/coach.registration";
import { AppTableName, ElementTypes } from "../global/global.model";
import { CompanyAffectationDTO, CompanyDTO } from "./company.dto";
import { CoachDTO, ProfessionalInfosDTO } from "./user.dto";
import { allWeekAvailability } from "./user.tools";
import { COMPANY_POSITION, JOB_TYPE, USER_TYPE } from "./user.types";


export const ProfessionalPositions = [COMPANY_POSITION.EMPLOYEE, COMPANY_POSITION.MANAGER];
export const CustomerPositions = [COMPANY_POSITION.CUSTOMER, COMPANY_POSITION.ARCHIVED];

/** Program Helper interfaced "abstractly" the methods to get program related DTO -- for higher performance, do not use async in getData parameter */
export class UserHelper {
    constructor(public getData: <T extends AppTableName>(tableName: T, filter?: { [K in keyof ElementTypes[T]]?: ElementTypes[T][K] }) => Promise<ElementTypes[T][]>) {

    }

    /** Patient is archived if its relation with the coach company(s) are archived */
    public async isPatientArchived(professionalId: ID, patientId: ID): Promise<boolean> {
        const join = await this.getPatientCompanyAffectationCommonWithProfessional(patientId, professionalId, ProfessionalPositions, [COMPANY_POSITION.ARCHIVED]);
        return join.length > 0;
    }

    /** 
     * Return every company affectation of the patient, in common with a professional (patient is indeed a patient of the professional through these)
     * @param patientId ID of patient
     * @param professionalId ID of professional
     * @param proPositions Filter professional positions to provided ones. Default is `[COMPANY_POSITION.MANAGER, COMPANY_POSITION.EMPLOYEE]`
     * @param patientPosition Filter patient positions to provided ones. Default is `[COMPANY_POSITION.CUSTOMER]`
     * */
    public async getPatientCompanyAffectationCommonWithProfessional(patientId: ID, professionalId: ID, proPositions = ProfessionalPositions, patientPosition = [COMPANY_POSITION.CUSTOMER]): Promise<CompanyAffectationDTO[]> {
        const [proCompanies, patientCompanies] = await Promise.all([
            this.getData("company_affectations", { userId: professionalId }),
            this.getData("company_affectations", { userId: patientId })
        ]);
        return UserHelper.getPatientCompanyAffectationCommonWithProfessional(proCompanies, patientCompanies, proPositions, patientPosition);
    }

    /**
     * Return patient company affectations in common with provided professional company affectations
     * @param proCompanies Array of professional company affectations 
     * @param patientCompanies Array of patient company affectations
     * @param proPositions Filter professional company affectations with provided positions. Default is `[COMPANY_POSITION.MANAGER, COMPANY_POSITION.EMPLOYEE]`
     * @param patientPosition Filter patient company affectations with provided positions. Default is `[COMPANY_POSITION.CUSTOMER, COMPANY_POSITION.ARCHIVED]`
     */
    public static getPatientCompanyAffectationCommonWithProfessional(proCompanies: CompanyAffectationDTO[], patientCompanies: CompanyAffectationDTO[], proPositions = ProfessionalPositions, patientPosition = CustomerPositions): CompanyAffectationDTO[] {
        const proCompanyIds: Set<ID> = new Set();
        for (const proCompany of proCompanies) {
            if (proPositions.includes(proCompany.position)) {
                proCompanyIds.add(proCompany.companyId);
            }
        }
        const join: CompanyAffectationDTO[] = [];
        for (const patientCompany of patientCompanies) {
            if (patientPosition.includes(patientCompany.position) && proCompanyIds.has(patientCompany.companyId)) {
                join.push(patientCompany);
            }
        }
        return join;
    }

    /**
     * Create all DTOs needed for a professional registration
     * @param coachRegistration ```CoachRegistrationParams``` object
     * @param calendarId Id of ```BookingCalendar``` object associated to user
     */
    public static getProfessionalDTOsFromUserRegistration(coachRegistration: CoachRegistrationParams, calendarId: ID): { user: CoachDTO, infos: ProfessionalInfosDTO } {
        return {
            user: {
                id: INIT_ID,
                creationDate: NOW(),
                defaultAvailabilities: allWeekAvailability,
                email: coachRegistration.email,
                familyName: coachRegistration.familyName,
                givenName: coachRegistration.givenName,
                modificationDate: NOW(),
                passwordResetRequired: false,
                phoneNumber: coachRegistration.phoneNumber,
                remindNotification: true,
                subscribeNotification: true,
                resetPasswordKey: "",
                resetPasswordKeyGenerationTime: toTimestamp(0),
                uid: coachRegistration.email,
                userType: USER_TYPE.COACH,
                bookingType: BOOKING_USER_TYPE.PROFESSIONAL,
                autoAccept: true,
                calendarId: calendarId,
                advertisement: _SAFE_SHARING.TIMESLOT_ONLY,
                consentTs: NOW(),
                __tableName: "user_profiles"
            },
            infos: {
                id: INIT_ID,
                userId: INIT_ID,// ensure to update  after user create
                __tableName: "professional_infos",
                jobType: JOB_TYPE.COACH,
                acceptReferencing: true
            }
        };
    }

    /** */
    public static isProfessional(companyAffectation: CompanyAffectationDTO): boolean {
        return ProfessionalPositions.includes(companyAffectation.position);
    }

    /** */
    public async getProfessionalInfosAndCompanies(): Promise<Map<CoachDTO, { companies: CompanyDTO[], info: ProfessionalInfosDTO }>> {
        const [coaches, allInfos, allCompanies, allCompanyAffectations] = await Promise.all([
            this.getData("user_profiles", { userType: USER_TYPE.COACH }),
            this.getData("professional_infos"),
            this.getData("companies"),
            this.getData("company_affectations")
        ]);
        const companyAffectationByUser = buildArrayMap(allCompanyAffectations, "userId");
        const infosByUser = buildObjectMap(allInfos, "userId");
        const companyById = buildObjectMap(allCompanies, "id");

        const r: Map<CoachDTO, { companies: CompanyDTO[], info: ProfessionalInfosDTO }> = new Map();
        for (const coach of coaches) {
            const info = infosByUser.get(coach.id);
            const companiyAffectations = companyAffectationByUser.get(coach.id);
            if (info && companiyAffectations) {
                const companies: CompanyDTO[] = [];
                for (const companiyAffectation of companiyAffectations) {
                    const company = companyById.get(companiyAffectation.companyId);
                    if (company) {
                        companies.push(company);
                    }
                }
                r.set(coach as CoachDTO, { companies, info });
            }
        }
        return r;
    }

}